import { Captions } from '$components/Hero/Captions';
import { HeroOverlay } from '$components/Hero/HeroOverlay';
import { Leader } from '$components/Hero/Leader';
import { Titles } from '$components/Hero/Titles';
import { HeroContext } from '$components/Hero/context';
import { ZeroElement } from '$components/ZeroElement';
import { LayoutContext } from '$context/index';
import cn from 'classnames';
import { HTMLAttributes, useContext, useEffect, useState } from 'react';

import { IHeroSlide } from './types';

export function HeroSlideImage(
  props: IHeroSlide & HTMLAttributes<HTMLElement>,
) {
  const useStore = useContext(HeroContext);
  const useLayoutStore = useContext(LayoutContext);
  const setMenuTheme = useLayoutStore((state) => state.setMenuTheme);
  const windowScroll = useLayoutStore((state) => state.windowScroll);
  const resized = useLayoutStore((state) => state.resized);

  const currentAnimation = useStore((state) => state.currentAnimation);
  const currentInnerAnimation = useStore(
    (state) => state.currentInnerAnimation,
  );
  const currentPage = useStore((state) => state.currentPage);
  const fromBG = useStore((state) => state.fromBG);
  const firstLoad = useStore((state) => state.firstLoad);
  const { className, num, children, slideAppearance, imageWidth, imageHeight } =
    props;

  const finalAsset =
    typeof children !== 'undefined' && children !== null
      ? children.toString()
      : '';
  const finalAssetWithUrl = 'url(' + finalAsset + ')';

  const [newHeight, setHeight] = useState(window.innerHeight);
  useEffect(() => {
    setHeight(parseInt(resized));
  }, [resized]);

  useEffect(() => {
    if (currentPage === num && windowScroll <= 0) {
      setMenuTheme(props.slideType);
    }
  }, [windowScroll, currentPage, num, props.slideType, setMenuTheme]);

  const finalImageWidth = imageWidth;
  const finalImageHeight = imageHeight;

  const canvasHeight = newHeight;
  const canvasWidth = window.innerWidth;

  const imgRatio = finalImageHeight / finalImageWidth;
  const canvasRatio = canvasHeight / canvasWidth;
  let finalHeight,
    finalWidth,
    finalHeightOuter,
    finalWidthOuter,
    scale,
    scaleMultiplier = 1.05,
    scaleMultiplierOuter = 0.85;

  if (canvasRatio > imgRatio) {
    finalHeight = canvasHeight * scaleMultiplier;
    scale = finalHeight / finalImageHeight;
    finalWidth = Math.round(finalImageWidth * scale);

    finalHeightOuter = canvasHeight * scaleMultiplierOuter;
    scale = finalHeightOuter / finalImageHeight;
    finalWidthOuter = Math.round(finalImageWidth * scale);
  } else {
    finalWidth = canvasWidth * scaleMultiplier;
    scale = finalWidth / finalImageWidth;
    finalHeight = Math.round(finalImageHeight * scale);

    finalWidthOuter = canvasWidth * scaleMultiplierOuter;
    scale = finalWidthOuter / finalImageWidth;
    finalHeightOuter = Math.round(finalImageHeight * scale);
  }

  const finalSize =
    newHeight > window.innerWidth ? newHeight + 'px' : window.innerWidth + 'px';

  const fadeOut =
    !firstLoad &&
    Math.abs(num - fromBG) <= 1 &&
    !(
      (currentPage < fromBG && fromBG < num) ||
      (currentPage < num && fromBG > num) ||
      (currentPage > num && fromBG < num) ||
      (currentPage > fromBG && fromBG > num)
    );

  return (
    <div
      key={num}
      id={`slide-${num + 1}`}
      className={cn(
        className,
        slideAppearance === 'insideRingFluid' ||
          slideAppearance === 'insideRingStatic'
          ? 'bg-white'
          : '',
        'p-gutter-mobile md:p-gutter-desktop w-full flex flex-col justify-center bg-no-repeat bg-cover absolute t-0 l-0',
        currentPage !== num ? 'pointer-events-none' : '',
        currentPage === num ? 'fadeIn' : fadeOut ? 'fadeOut' : '',
        'h-full',
      )}
      style={{
        opacity: currentPage === num ? '0' : fadeOut ? '1' : '0',
      }}
    >
      <div className={cn('relative z-20 overflow-hidden')}>
        <Leader {...props} />
        <Titles {...props} />
        <Captions currentPage={currentPage} {...props} />
      </div>
      {slideAppearance !== 'noRing' && (
        <>
          {(slideAppearance === 'insideRingFluid' ||
            slideAppearance === 'insideRingStatic') && (
            <div
              className={cn(
                currentPage === num ? 'visible' : 'invisible',
                'productSwirlContainer flex-col absolute left-0 top-0 w-full md:h-full flex md:justify-center items-center md:overflow-hidden duration-[1600ms] h-full',
              )}
            >
              <div
                style={{
                  animationName: currentAnimation,
                }}
                className={cn('outerRing absolute left-0 top-0 w-full h-full')}
              >
                <ZeroElement
                  key={num + 1}
                  assetType={'image'}
                  type={
                    slideAppearance === 'insideRingFluid'
                      ? 'fluid'
                      : slideAppearance === 'insideRingStatic'
                      ? 'static'
                      : 'empty'
                  }
                  videoAsset={null}
                  imageAsset={
                    slideAppearance === 'insideRingFluid' ||
                    slideAppearance === 'insideRingStatic'
                      ? finalAsset
                      : null
                  }
                />
              </div>
            </div>
          )}
          {slideAppearance !== 'insideRingFluid' &&
            slideAppearance !== 'insideRingStatic' && (
              <div className={cn('swirlContainer h-full')}>
                <div
                  data-from={fromBG}
                  data-current={currentPage}
                  style={{
                    animationName: currentAnimation,
                    height: finalSize,
                    width: finalSize,
                  }}
                  className={cn('outerContainer absolute')}
                >
                  <div
                    style={{
                      backgroundRepeat: 'repeat',
                      backgroundImage: finalAssetWithUrl,
                      animationName: currentInnerAnimation,
                      backgroundSize:
                        finalWidthOuter + 'px ' + finalHeightOuter + 'px',
                      height: finalSize,
                      width: finalSize,
                    }}
                    className={cn('absolute')}
                  ></div>
                </div>
                <div
                  style={{
                    backgroundImage: finalAssetWithUrl,
                    animationName: currentAnimation,
                    backgroundSize: finalWidth + 'px ' + finalHeight + 'px',
                  }}
                  className={cn(
                    'outerRing absolute h-[72vh] md:h-[85vh] w-[72vh] md:w-[85vh]',
                  )}
                ></div>
                <div
                  style={{
                    backgroundImage: finalAssetWithUrl,
                    animationName: currentAnimation,
                    backgroundSize: finalWidth + 'px ' + finalHeight + 'px',
                  }}
                  className={cn(
                    'innerRing absolute h-[36vh] md:h-[42.5vh] w-[36vh] md:w-[42.5vh]',
                  )}
                ></div>
              </div>
            )}
          {props.overlayText !== '' && (
            <HeroOverlay {...props} currentPage={currentPage} num={num} />
          )}
        </>
      )}
      {slideAppearance === 'noRing' && (
        <>
          <div className={cn('swirlContainer h-full')}>
            <div
              style={{
                backgroundImage: finalAssetWithUrl,
                backgroundSize: finalWidth + 'px ' + finalHeight + 'px',
                height: finalSize,
                width: finalSize,
              }}
              className={cn('outerContainer absolute')}
            ></div>
          </div>
        </>
      )}
    </div>
  );
}
